import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import {
  AccessorColumnDef,
  type Cell,
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
  closestCorners,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { TableSearch, TableActions, RowSelector, DraggableRowHandle, DraggableRow, TagList } from "./table/index";
import { Z_INDEX_CLASSES } from "../lib/z-index";
import type { ActionButtonConfig, FilterConfig, EnhancedSearchConfig, TagListColumnConfig } from "./data-table";
import { useTranslation } from "react-i18next";
import { useUncontrolledState } from "../hooks";
import { cn } from "@tellescope/utils";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Safely extracts the column ID from a VirtualizedColumnDef.
 * Tries id first, then falls back to accessorKey.
 */
function getColumnId<TData>(col: VirtualizedColumnDef<TData>): string {
  if ("id" in col && col.id) {
    return col.id;
  }
  if ("accessorKey" in col && col.accessorKey) {
    return String(col.accessorKey);
  }
  return "unknown";
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Unified column definition that combines TanStack Table's ColumnDef
 * with UI metadata for rendering and layout.
 */
export interface VirtualizedColumnDef<TData> extends Omit<AccessorColumnDef<TData>, "size"> {
  /** Header label text */
  label?: string;

  /** Column width - flexible (e.g., '1fr', '2fr') or fixed (e.g., 160 for 160px) */
  width?: number | string;

  /** Minimum width in pixels */
  minWidth?: number;

  /** Enable sorting for this column (default: true) */
  sortable?: boolean;

  /** Initial visibility state (default: true) */
  visible?: boolean;

  /** Special column type for automatic rendering */
  type?: "default" | "tagList";

  /** Configuration for tag list columns */
  tagListConfig?: TagListColumnConfig;

  /** Pin column to left or right (rare use case) */
  pinned?: "left" | "right";

  /** Text alignment */
  align?: "left" | "center" | "right";
}

/**
 * Props for VirtualizedDataTable component
 */
export interface VirtualizedDataTableProps<TData> {
  // ========== Core (required) ==========
  /** Table data */
  data: TData[];

  /** Unified column configuration */
  columns: VirtualizedColumnDef<TData>[];

  /** Function to extract unique ID from row data (default: row.id || String(row)) */
  getRowId?: (row: TData) => string;

  // ========== Features (optional with smart defaults) ==========
  /** Enable sorting (default: true) */
  enableSorting?: boolean;

  /** Enable client-side sorting of data (default: true).
   *  Set to false when sorting is handled server-side. Sort header clicks
   *  still update the sorting state (and onSortingChange is still called),
   *  but TanStack Table's getSortedRowModel is not applied. */
  enableInternalSorting?: boolean;

  /** Enable search/filtering (default: true) */
  enableSearch?: boolean;

  /** Enable client-side filtering of data when searching (default: true).
   *  Set to false when filtering is handled server-side. The search input
   *  remains visible and onGlobalFilterChange is still called, but
   *  TanStack Table's getFilteredRowModel is not applied. */
  enableInternalFiltering?: boolean;

  /** Enable row selection (default: false) */
  enableSelection?: boolean;

  /** Enable drag & drop (default: false) */
  enableDragDrop?: boolean;

  // ========== Search ==========
  /** Search input placeholder */
  searchPlaceholder?: string;

  /** Controlled search value */
  globalFilter?: string;

  /** Search value change handler */
  onGlobalFilterChange?: (value: string) => void;

  /** Enhanced search configuration for cross-entity searching */
  enhancedSearch?: EnhancedSearchConfig;

  // ========== Selection ==========
  /** Currently selected row IDs */
  selectedRows?: string[];

  /** Handler for individual row selection */
  onRowSelect?: (rowId: string) => void;

  /** Handler for select all */
  onSelectAll?: () => void;

  // ========== Column Visibility ==========
  /** Column visibility state (key: visible) */
  columnVisibility?: Record<string, boolean>;

  /** Handler for column visibility toggle (passes column key, parent handles toggle logic) */
  onToggleColumn?: (columnKey: string) => void;

  // ========== Virtualization ==========
  /** Row height in pixels (default: 53) */
  rowHeight?: number;

  /** Number of rows to render outside viewport (default: 5) */
  overscan?: number;

  // ========== Height Configuration (pick ONE) ==========
  /** Fixed height as number (px) or CSS string (e.g., '60vh') */
  height?: string | number;

  /** Viewport-relative height (e.g., { offset: 280 } = calc(100vh - 280px)) */
  viewportHeight?: { offset: number };
  // Default: '400px' if neither provided

  // ========== Infinite Scroll ==========
  /** Whether more data is available */
  hasMoreData?: boolean;

  /** Whether currently loading more data */
  isLoadingMore?: boolean;

  /** Handler to load more data */
  onLoadMore?: () => Promise<void> | void;

  isEmpty?: boolean;

  // ========== Interactions ==========
  /** Handler for row click */
  onRowClick?: (row: TData) => void;

  /** Handler for row right-click */
  onRowRightClick?: (row: TData) => void;

  /** Handler for row hover */
  onRowHover?: (rowId: string | null) => void;

  /** Currently hovered row ID */
  hoveredRowId?: string | null;

  // ========== Drag & Drop ==========
  /**
   * Callback when items are reordered via drag-drop.
   * Called with an array of { id, index } objects representing the new order.
   * Only invoked when drag ends with a valid drop.
   */
  onReorder?: (reorderedItems: Array<{ id: string; index: number }>) => void;

  // ========== UI ==========
  /** Action buttons in header */
  actionButtons?: ActionButtonConfig[];

  /** Filter configuration */
  filterConfig?: FilterConfig;

  /** Additional CSS classes */
  className?: string;

  /** Optional controlled sorting state */
  sorting?: SortingState;

  /** Optional controlled sorting state setter */
  setSorting?: Dispatch<SetStateAction<SortingState>>;

  tableClassName?: string;
  headerClassName?: string;

  /** Maximum number of rows to display (default: Infinity) */
  maxDisplayRows?: number;

  hideFooter?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function VirtualizedDataTable<TData>({
  // Core
  data,
  columns,
  getRowId = (row: TData) => {
    // Try to extract id property if it exists
    if (row && typeof row === "object" && "id" in row) {
      return String((row as { id: unknown }).id);
    }
    return String(row);
  },

  // Features
  enableSorting = true,
  enableInternalSorting = true,
  enableSearch = true,
  enableInternalFiltering = true,
  enableSelection = false,
  enableDragDrop = false,

  // Search
  searchPlaceholder = "Search...",
  globalFilter = "",
  onGlobalFilterChange,
  enhancedSearch,

  // Selection
  selectedRows = [],
  onRowSelect,
  onSelectAll,

  // Column visibility
  columnVisibility = {},
  onToggleColumn,

  // Virtualization
  rowHeight = 53,
  overscan = 5,

  // Height
  height,
  viewportHeight,

  // Infinite scroll
  hasMoreData = false,
  isLoadingMore = false,
  onLoadMore,
  isEmpty,

  // Interactions
  onRowClick,
  onRowRightClick,
  onRowHover,
  hoveredRowId,

  // Drag & drop
  onReorder,

  // UI
  actionButtons = [],
  filterConfig,
  className = "",

  sorting: providedSorting,
  setSorting: providedSetSorting,
  tableClassName,
  headerClassName,
  maxDisplayRows = Infinity,
  hideFooter,
}: VirtualizedDataTableProps<TData>) {
  const { t } = useTranslation("ui");

  // ========== State ==========
  const [sorting, setSorting] = useUncontrolledState({
    state: providedSorting ?? [],
    setState: providedSetSorting,
  });
  const isSortingActive = sorting.length > 0;
  const canReorder = enableDragDrop && !isSortingActive;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState(globalFilter);
  const [rowSelection, setRowSelection] = useState({});

  // Use external or internal global filter state
  const activeGlobalFilter = onGlobalFilterChange ? globalFilter : internalGlobalFilter;
  const setActiveGlobalFilter = onGlobalFilterChange || setInternalGlobalFilter;

  // ========== Enhanced Search ==========
  const performEnhancedSearch = useCallback(
    (records: TData[], searchQuery: string): TData[] => {
      if (!searchQuery.trim() || !enhancedSearch) {
        return records;
      }

      const query = searchQuery.toLowerCase();

      return records.filter((record) => {
        // Search primary fields
        const primaryMatches = (enhancedSearch.primaryFields || []).some((field) => {
          const value = (record as Record<string, unknown>)[field];
          return value && String(value).toLowerCase().includes(query);
        });

        if (primaryMatches) return true;

        // Search joined fields
        const joinedMatches = (enhancedSearch.joinedFields || []).some((joinedField) => {
          const sourceValue = (record as Record<string, unknown>)[joinedField.sourceField];
          if (!sourceValue) return false;

          // Handle both single values and arrays
          const sourceIds = Array.isArray(sourceValue) ? sourceValue : [sourceValue];

          // Get the related record from the target slice
          const targetData = joinedField.getTargetData();

          // Search within all related records
          return sourceIds.some((id) => {
            const relatedRecord = targetData[id];
            if (!relatedRecord) return false;

            // Search within the related record's specified fields
            return joinedField.searchFields.some((searchField) => {
              const fieldValue = relatedRecord[searchField];
              return fieldValue && String(fieldValue).toLowerCase().includes(query);
            });
          });
        });

        return joinedMatches;
      });
    },
    [enhancedSearch]
  );

  // Debounced search term to prevent rapid virtualizer updates
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(activeGlobalFilter);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(activeGlobalFilter);
    }, 150);

    return () => clearTimeout(timer);
  }, [activeGlobalFilter]);

  // Apply enhanced search filtering if configured (skip when client-side filtering is disabled)
  const filteredData = useMemo(() => {
    if (enableInternalFiltering && enhancedSearch) {
      return performEnhancedSearch(data, debouncedSearchTerm);
    }
    return data;
  }, [data, debouncedSearchTerm, performEnhancedSearch, enhancedSearch, enableInternalFiltering]);

  // For drag and drop, we don't do optimistic reordering during drag
  // We let dnd-kit handle the visual reordering via CSS transforms
  // and only update data on drag end
  const displayData = filteredData;

  const onSortingChange = useCallback<NonNullable<Parameters<typeof useReactTable>[0]["onSortingChange"]>>(
    (newSorting) => setSorting(newSorting),
    [setSorting]
  );

  // ========== TanStack Table Setup ==========
  const table = useReactTable({
    data: displayData,
    columns: columns as ColumnDef<TData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting && enableInternalSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableInternalFiltering && enableSearch && !enhancedSearch ? getFilteredRowModel() : undefined,
    onSortingChange,
    onColumnFiltersChange: enableInternalFiltering && enableSearch && !enhancedSearch ? setColumnFilters : undefined,
    onGlobalFilterChange: setActiveGlobalFilter,
    onRowSelectionChange: enableSelection ? setRowSelection : undefined,
    getRowId: (row) => getRowId(row),
    state: {
      sorting: enableSorting ? sorting : undefined,
      columnFilters: enableInternalFiltering && enableSearch && !enhancedSearch ? columnFilters : undefined,
      globalFilter: !enableInternalFiltering
        ? undefined
        : enhancedSearch
        ? ""
        : enableSearch
        ? activeGlobalFilter
        : undefined,
      rowSelection: enableSelection ? rowSelection : undefined,
    },
  });

  // ========== Virtualization Setup ==========
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const allTableRows = table.getRowModel().rows;
  const isRowCapped = maxDisplayRows < allTableRows.length;
  const tableData = isRowCapped ? allTableRows.slice(0, maxDisplayRows) : allTableRows;

  const getScrollContainer = useCallback(() => {
    return scrollContainerRef.current;
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: tableData.length,
    getScrollElement: getScrollContainer,
    estimateSize: () => rowHeight,
    overscan,
    measureElement: (el) => el?.getBoundingClientRect().height,
  });

  // ========== Infinite Scroll ==========
  useEffect(() => {
    const container = getScrollContainer();
    // Disable infinite scroll when maxDisplayRows is already capping the displayed rows
    if (!container || !onLoadMore || !hasMoreData || isLoadingMore || isRowCapped) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      // If there's no scrollbar, don't trigger loading
      if (scrollHeight <= clientHeight) return;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Trigger loading when user scrolls past 80%
      if (scrollPercentage > 0.8) {
        onLoadMore();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [getScrollContainer, onLoadMore, hasMoreData, isLoadingMore, isRowCapped]);

  // ========== DnD-Kit Setup ==========
  const dataIds = useMemo<UniqueIdentifier[]>(() => tableData.map((row) => row.id), [tableData]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required to start drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {})
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!canReorder) return;

      const { active, over } = event;

      // active.id and over.id are the Row IDs (strings)
      if (active && over && active.id !== over.id) {
        // 1. Find indices within the CURRENT view (filteredData)
        const oldIndex = filteredData.findIndex((item) => String(getRowId(item)) === String(active.id));
        const newIndex = filteredData.findIndex((item) => String(getRowId(item)) === String(over.id));

        // 2. Only move if both items exist in the current filtered view
        if (oldIndex !== -1 && newIndex !== -1) {
          const newData = arrayMove(filteredData, oldIndex, newIndex);

          if (onReorder) {
            // 3. Map to the format the parent expects
            const reorderedItems = newData.map((item, index) => ({
              id: getRowId(item),
              index,
            }));
            onReorder(reorderedItems);
          }
        }
      }
    },
    [canReorder, filteredData, getRowId, onReorder]
  );

  // ========== Event Handlers ==========
  const handleRowClick = (row: TData) => {
    onRowClick?.(row);
  };

  const handleRowRightClick = (row: TData) => {
    onRowRightClick?.(row);
  };

  const handleRowSelect = (rowId: string) => {
    onRowSelect?.(rowId);
  };

  const handleSelectAll = () => {
    onSelectAll?.();
  };

  const handleRowHover = (rowId: string | null) => {
    onRowHover?.(rowId);
  };

  // ========== Column Rendering ==========
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => {
      const columnId = getColumnId(col);
      return columnVisibility[columnId] !== false && col.visible !== false;
    });
  }, [columns, columnVisibility]);

  const renderCell = useCallback(
    (cell: Cell<TData, unknown>, columnDef: VirtualizedColumnDef<TData>) => {
      // Special handling for tagList columns
      if (columnDef.type === "tagList" && columnDef.tagListConfig) {
        const rowData = cell.row.original;
        const rowId = getRowId(rowData);
        const tags = (cell.getValue() as string[]) || [];

        return (
          <TagList
            items={tags}
            variant={columnDef.tagListConfig.variant || "secondary"}
            onRemoveTag={
              columnDef.tagListConfig.onRemoveTag
                ? (tag: string) => columnDef.tagListConfig!.onRemoveTag!(rowId, tag)
                : undefined
            }
            onAddTag={
              columnDef.tagListConfig.onAddTag
                ? (tag: string) => columnDef.tagListConfig!.onAddTag!(rowId, tag)
                : undefined
            }
            availableOptions={columnDef.tagListConfig.availableOptions}
            allowColorSelection={columnDef.tagListConfig.allowColorSelection}
            globalTagColors={columnDef.tagListConfig.globalTagColors}
            onSetGlobalTagColor={columnDef.tagListConfig.onSetGlobalTagColor}
          />
        );
      }

      // Default: Use TanStack Table's flexRender
      return flexRender(cell.column.columnDef.cell, cell.getContext());
    },
    [getRowId]
  );

  // ========== Height Configuration ==========
  const containerHeight = useMemo(() => {
    if (height !== undefined) {
      return typeof height === "number" ? `${height}px` : height;
    }
    if (viewportHeight) {
      return `calc(100vh - ${viewportHeight.offset}px)`;
    }
    return "400px"; // Default
  }, [height, viewportHeight]);

  // ========== Grid Template Columns ==========
  const gridTemplateColumns = useMemo(() => {
    const columnWidths: string[] = [];

    // Drag & drop column
    if (canReorder) {
      columnWidths.push("40px");
    }

    // Selection column
    if (enableSelection) {
      columnWidths.push("40px");
    }

    // Data columns
    visibleColumns.forEach((col) => {
      if (typeof col.width === "number") {
        columnWidths.push(`${col.width}px`);
      } else if (typeof col.width === "string") {
        columnWidths.push(col.width);
      } else {
        columnWidths.push("1fr");
      }
    });

    return columnWidths.join(" ");
  }, [enableSelection, canReorder, visibleColumns]);

  // ========== Render ==========
  const totalHeight = rowVirtualizer.getTotalSize();
  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <div className={`w-full ${className}`}>
      {/* Header Controls */}
      <div className="flex items-center justify-between pb-4">
        <div className="flex-1">
          {(enableSearch || filterConfig) && (
            <TableSearch
              placeholder={searchPlaceholder}
              value={activeGlobalFilter ?? ""}
              onChange={setActiveGlobalFilter}
              showFilter={filterConfig?.showFilter}
              filterLabel={filterConfig?.label}
              filterItems={filterConfig?.items}
              selectedFilters={filterConfig?.selectedFilters}
              onFilterSelectionChange={filterConfig?.onFilterSelectionChange}
              filterPlaceholder={filterConfig?.placeholder}
            />
          )}
        </div>

        <div className="flex-shrink-0">
          <TableActions
            columnSelector={
              onToggleColumn
                ? {
                    columns: columns.map((col) => ({
                      key: getColumnId(col),
                      label: col.label || "",
                      width: typeof col.width === "number" ? `${col.width}px` : col.width || "auto",
                    })),
                    visibleColumns: columnVisibility,
                    onToggleColumn: onToggleColumn,
                  }
                : undefined
            }
            buttons={actionButtons}
          />
        </div>
      </div>

      {/* Virtualized Table Container */}
      <div className={cn("border rounded-lg overflow-hidden", tableClassName)}>
        {/* Header */}
        <div
          className={cn(
            "grid items-center h-12 px-4 text-sm font-medium border-b bg-background sticky top-0 min-h-[48px]",
            Z_INDEX_CLASSES.tableHeader,
            headerClassName
          )}
          style={{ gridTemplateColumns }}
          data-testid="virtualized-table-header"
        >
          {/* Drag & drop header */}
          {canReorder && <div />}

          {/* Selection header */}
          {enableSelection && (
            <div className="flex items-center justify-center">
              <RowSelector
                checked={selectedRows.length === tableData.length && tableData.length > 0}
                onCheckedChange={handleSelectAll}
                showCheckbox={true}
                showHoverIcon={false}
              />
            </div>
          )}

          {/* Column headers */}
          {visibleColumns.map((col) => {
            const columnId = getColumnId(col);
            const column = table.getColumn(columnId);
            const isSorted = column?.getIsSorted();

            return (
              <div
                key={columnId}
                className={`flex items-center overflow-hidden ${
                  col.align === "right" ? "justify-end" : col.align === "center" ? "justify-center" : ""
                }`}
              >
                {col.sortable !== false && enableSorting && column ? (
                  <button
                    onClick={() => {
                      if (!isSorted) {
                        // No sorting → asc
                        column.toggleSorting(false);
                      } else if (isSorted === "asc") {
                        // asc → desc
                        column.toggleSorting(true);
                      } else {
                        // desc → no sorting (clear)
                        column.clearSorting();
                      }
                    }}
                    className="flex items-center gap-1 hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 text-left w-full"
                    data-testid={`column-header-${columnId}`}
                  >
                    {col.label}
                    {isSorted === "desc" ? (
                      <ArrowUp className="ml-2 h-4 w-4" data-testid="sort-icon-desc" />
                    ) : isSorted === "asc" ? (
                      <ArrowDown className="ml-2 h-4 w-4" data-testid="sort-icon-asc" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 opacity-40" />
                    )}
                  </button>
                ) : (
                  <span
                    className={`py-1 w-full ${
                      col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                    }`}
                  >
                    {col.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Virtual Body */}
        <div ref={scrollContainerRef} className="overflow-auto" style={{ height: containerHeight }}>
          {canReorder ? (
            <DndContext
              collisionDetection={closestCorners}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                <div
                  style={{
                    height: totalHeight,
                    position: "relative",
                    minHeight: tableData.length * rowHeight,
                  }}
                >
                  {virtualItems.map((virtualRow) => {
                    const row = tableData[virtualRow.index];
                    if (!row) {
                      return null;
                    }
                    const rowData = row.original;
                    const rowId = getRowId(rowData);

                    return (
                      <DraggableRow
                        key={rowId}
                        rowId={rowId}
                        data-index={virtualRow.index}
                        className={`absolute left-0 w-full border-b bg-background hover:bg-muted/50${
                          onRowClick ? " cursor-pointer" : ""
                        } group`}
                        style={{
                          height: virtualRow.size,
                          top: virtualRow.start,
                          minHeight: rowHeight,
                        }}
                        onClick={() => handleRowClick(rowData)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          handleRowRightClick(rowData);
                        }}
                        onMouseEnter={() => handleRowHover(rowId)}
                        onMouseLeave={() => handleRowHover(null)}
                      >
                        <div className="grid items-center h-full px-4" style={{ gridTemplateColumns }}>
                          {/* Drag & drop cell */}
                          {canReorder && (
                            <div className="flex items-center justify-center">
                              <DraggableRowHandle rowId={rowId} />
                            </div>
                          )}

                          {/* Selection cell */}
                          {enableSelection && (
                            <div className="flex items-center justify-center">
                              <RowSelector
                                checked={selectedRows.includes(rowId)}
                                onCheckedChange={() => handleRowSelect(rowId)}
                                showCheckbox={true}
                                showHoverIcon={true}
                                isHovered={hoveredRowId === rowId}
                              />
                            </div>
                          )}

                          {/* Data cells */}
                          {visibleColumns.map((col) => {
                            const columnId = getColumnId(col);
                            const cell = row.getVisibleCells().find((c) => c.column.id === columnId);

                            return (
                              <div
                                key={columnId}
                                className={`flex items-center overflow-hidden py-1 ${
                                  col.align === "right" ? "justify-end" : col.align === "center" ? "justify-center" : ""
                                }`}
                              >
                                {cell ? renderCell(cell, col) : null}
                              </div>
                            );
                          })}
                        </div>
                      </DraggableRow>
                    );
                  })}

                  {/* Loading indicator */}
                  {isLoadingMore && (
                    <div
                      className="absolute top-0 left-0 w-full flex items-center justify-center py-4 bg-background/80 border-b"
                      style={{
                        transform: `translateY(${totalHeight}px)`,
                        height: rowHeight,
                      }}
                    >
                      <div className="text-sm text-muted-foreground">{t("Loading more...")}</div>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div
              style={{
                height: totalHeight,
                position: "relative",
                minHeight: tableData.length * rowHeight,
              }}
            >
              {virtualItems.map((virtualRow) => {
                const row = tableData[virtualRow.index];
                if (!row) return null;
                const rowData = row.original;
                const rowId = getRowId(rowData);

                return (
                  <div
                    key={rowId}
                    data-index={virtualRow.index}
                    className={`absolute top-0 left-0 w-full border-b bg-background ${
                      hoveredRowId === rowId ? "bg-muted/50" : ""
                    } ${onRowClick ? "cursor-pointer" : ""}`}
                    style={{
                      height: virtualRow.size,
                      transform: `translateY(${virtualRow.start}px)`,
                      minHeight: rowHeight,
                    }}
                    onClick={() => handleRowClick(rowData)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleRowRightClick(rowData);
                    }}
                    onMouseEnter={() => handleRowHover(rowId)}
                    onMouseLeave={() => handleRowHover(null)}
                  >
                    <div className="grid items-center h-full px-4" style={{ gridTemplateColumns }}>
                      {/* Drag & drop cell */}
                      {canReorder && (
                        <div className="flex items-center justify-center">
                          <DraggableRowHandle rowId={rowId} />
                        </div>
                      )}

                      {/* Selection cell */}
                      {enableSelection && (
                        <div className="flex items-center justify-center">
                          <RowSelector
                            checked={selectedRows.includes(rowId)}
                            onCheckedChange={() => handleRowSelect(rowId)}
                            showCheckbox={true}
                            showHoverIcon={false}
                            isHovered={hoveredRowId === rowId}
                          />
                        </div>
                      )}

                      {/* Data cells */}
                      {visibleColumns.map((col) => {
                        const columnId = getColumnId(col);
                        const cell = row.getVisibleCells().find((c) => c.column.id === columnId);

                        return (
                          <div
                            key={columnId}
                            className={`flex items-center overflow-hidden py-1 ${
                              col.align === "right" ? "justify-end" : col.align === "center" ? "justify-center" : ""
                            }`}
                          >
                            {cell ? renderCell(cell, col) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Loading indicator */}
              {(isLoadingMore || isEmpty) && (
                <div
                  className="absolute top-0 left-0 w-full flex items-center justify-center py-4 bg-background/80 border-b"
                  style={{
                    transform: `translateY(${totalHeight}px)`,
                    height: rowHeight,
                  }}
                >
                  <div className="text-sm text-muted-foreground">
                    {isLoadingMore ? (data.length ? t("Loading more...") : t("Loading...")) : t("No data to display")}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status footer */}
      {!hideFooter && (
        <div className="flex items-center justify-between py-2 text-sm text-muted-foreground">
          <div>
            {isRowCapped
              ? t("Showing {{count}} of {{total}} items", { count: tableData.length, total: allTableRows.length })
              : hasMoreData
              ? t("Showing {{count}} items (more available)", { count: tableData.length })
              : t("Showing {{count}} items", { count: tableData.length })}
          </div>
          {isLoadingMore && !isRowCapped && <div>{t("Loading...")}</div>}
        </div>
      )}
    </div>
  );
}
